/**
 * BIGLI-33：報價單 / 出貨單 header 的公司抬頭，改為「依該張單據的門市」取值。
 *
 * 背景：RENT-56 起 header 讀的是公司層級的 templates.companyName / companyAddress /
 * companyPhone / companyEmail，全公司共用一份。門市各自有抬頭之後，這份共用值
 * 只能當回退，實際要顯示的是 MessageTemplates.storeProfiles 裡對應該門市的那一筆。
 *
 * 為什麼抽成共用函式而不是各自在兩支元件裡寫：
 *   報價單與出貨單是同一張單子的兩個面，抬頭長不一樣會被當成兩間公司開的單
 *   （BIGLI-13 的老問題）。取值規則只留一份，兩邊就不可能各自漂移。
 *
 * 回退規則（AC #8）——門市名稱在報價單／器材上是**字串快照**，公司設定改名或刪除門市
 * 都不會回頭改舊單據，所以「對不到」是正常且長期存在的狀態，不是錯誤：
 *   1. quotation.store 為空（舊報價單）              → 公司層級 company* 值
 *   2. 該門市在 storeProfiles 找不到（已刪除／改名）→ 公司層級 company* 值
 *   3. 找到了但該欄留白                              → 逐欄回退到公司層級，
 *      避免只填了公司名稱的門市把電話／信箱整排洗成空白
 *   4. 公司層級也沒有                                → 空字串（絕不回傳 undefined，
 *      否則 header 會印出字面上的 "undefined"）
 */

// header 上那四行的欄位名，storeProfiles 與公司層級共用同一組 key。
export const DOCUMENT_HEADER_FIELDS = [
  'companyName',
  'companyPhone',
  'companyEmail',
  'companyAddress',
];

const asTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * 依門市取單據 header 的公司抬頭。
 *
 * @param {Object} templates 公司設定（TemplatesContext 的 templates）
 * @param {string} store     該張單據的門市（quotation.store，字串快照）
 * @returns {{companyName: string, companyPhone: string, companyEmail: string, companyAddress: string}}
 *          四個欄位必為字串，永不為 undefined
 */
export const resolveDocumentHeader = (templates, store) => {
  const source = templates && typeof templates === 'object' ? templates : {};
  const profiles = Array.isArray(source.storeProfiles) ? source.storeProfiles : [];
  const wanted = asTrimmedString(store);

  const profile = wanted
    ? profiles.find((p) => p && asTrimmedString(p.store) === wanted)
    : null;

  return DOCUMENT_HEADER_FIELDS.reduce((acc, field) => {
    acc[field] = (profile && asTrimmedString(profile[field])) || asTrimmedString(source[field]);
    return acc;
  }, {});
};

/**
 * BIGLI-34：依門市取單據 header 的 LOGO。
 *
 * 與上面的抬頭同一套「門市 → 公司 → 內建」三段回退，但多一段內建圖：
 * 抬頭四欄可以是空字串（版面上就是少一行），LOGO 不行——<img src=""> 會在單據左上角
 * 留一個破圖 icon，PDF 匯出後尤其明顯。所以最後一定回到打包進 bundle 的 logo-export.png。
 *
 *   1. quotation.store 對得到 storeLogos 且該筆有圖 → 該門市的 logo
 *   2. 對不到（舊單據沒有 store／門市已刪除或改名）或該門市沒設圖 → companyLogoBase64
 *   3. 公司層級也沒設 → 呼叫端傳進來的內建 logo-export.png
 *
 * 沒有門市欄位的畫面（側欄、首頁、請款單、簽署頁）不走這裡，維持只讀公司 logo。
 *
 * @param {Object} templates    公司設定（TemplatesContext 的 templates）
 * @param {string} store        該張單據的門市（字串快照）
 * @param {string} fallbackLogo 內建 logo-export.png 的 import 結果
 * @returns {string} 可直接放進 <img src> 的字串，永不為空
 */
export const resolveDocumentLogo = (templates, store, fallbackLogo) => {
  const source = templates && typeof templates === 'object' ? templates : {};
  const logos = Array.isArray(source.storeLogos) ? source.storeLogos : [];
  const wanted = asTrimmedString(store);

  const entry = wanted
    ? logos.find((l) => l && asTrimmedString(l.store) === wanted)
    : null;

  return (
    (entry && asTrimmedString(entry.logoBase64)) ||
    asTrimmedString(source.companyLogoBase64) ||
    fallbackLogo
  );
};

/**
 * BIGLI-35：依門市取 Line 客服連結。
 *
 * PM Bridget 2026-09-01：「Line 客服連結的功能移到門市，各門市都會有一個 Line 客服」。
 * 公司層級的 lineService 沒有被刪除，改當回退值——與上面抬頭四欄同一套規則，
 * 差別只在這裡只有一個欄位：
 *   1. 單據有門市且該門市 storeProfiles 有填 lineService → 用門市的
 *   2. 單據沒門市（請款單沒有 store 欄、舊報價單也可能沒填）、門市已刪除／改名，
 *      或門市那欄留白                                      → 公司層級 lineService
 *   3. 兩邊都沒有                                           → 空字串
 *
 * 回傳空字串而不是 undefined：呼叫端會把它塞進 <a href="...">，undefined 會在單據上
 * 印出字面的 "undefined"（BIGLI-33 的老坑）。呼叫端要自己決定「空字串就整行不印」。
 *
 * @param {Object} templates 公司設定（TemplatesContext 的 templates）
 * @param {string} store     該張單據的門市（字串快照）
 * @returns {string} 永不為 undefined
 */
export const resolveStoreLineService = (templates, store) => {
  const source = templates && typeof templates === 'object' ? templates : {};
  const profiles = Array.isArray(source.storeProfiles) ? source.storeProfiles : [];
  const wanted = asTrimmedString(store);

  const profile = wanted
    ? profiles.find((p) => p && asTrimmedString(p.store) === wanted)
    : null;

  return (profile && asTrimmedString(profile.lineService)) || asTrimmedString(source.lineService);
};

export default resolveDocumentHeader;
